/**
 * useCapabilities — the client-side capability check every gated control reads.
 *
 * Source of truth, in priority order (see api/capabilities.ts):
 *   1. The SERVER-resolved list from Oxbo-M `GET /users/me/capabilities`
 *      (mApi.getMyCapabilities), once it has loaded successfully.
 *   2. FALLBACK to the legacy role→caps derivation while that query is still
 *      loading OR if it errored — so the SPA never regresses below todays
 *      behaviour and never flash-hides a feature during load. The server is the
 *      real gate regardless; this check is belt-and-suspenders (§22c).
 *
 * `can(cap)` uses the same wildcard semantics as the server resolver
 * (`capsSatisfy`), so `catalog:manage` is satisfied by holding `catalog:*:write`.
 */
import { useCallback } from "react";
import { useAppSelector } from "@app/hooks";
import { useGetMyCapabilitiesQuery } from "@api/mApi";
import { roleCapabilities, capsSatisfy } from "@api/capabilities";

export function useCapabilities() {
  const role = useAppSelector((s) => s.prefs.role);
  // Fetch the server-resolved caps. Hook is always called (hooks rules); RTK
  // dedupes so every consumer shares one in-flight request + cache entry.
  const { data: serverCaps, isSuccess, isError } = useGetMyCapabilitiesQuery();

  // Use the server list once it is known-good; otherwise fall back to the
  // role-derived set (fail-open-to-today). `isError` also falls back.
  const caps = isSuccess && serverCaps ? serverCaps : roleCapabilities(role);

  /** Does the current user hold `capability`? Uses the same caps model + wildcard
   * semantics the server gates on, so client + server agree (belt-and-suspenders). */
  const can = useCallback(
    (capability: string | undefined): boolean => {
      if (!capability) return false;
      return capsSatisfy(caps, capability);
    },
    [caps],
  );

  return { role, can, caps, capsReady: isSuccess, capsError: isError };
}
